// ============================================================================
// FloatingGlossary — global stats glossary dialog.
// Opens via the book button in the top bar (window event 'tc-open-glossary')
// or Cmd/Ctrl+G. Content is the shared GlossaryContent renderer; definitions
// live in components/glossary/glossaryData.js.
// ============================================================================
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, IconButton, Divider,
  Slide, useTheme, useMediaQuery,
} from '@mui/material';
import { MenuBook, Close } from '@mui/icons-material';
import GlossaryContent from './glossary/GlossaryContent';

const FloatingGlossary = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);

  // Open via global event (book button in the top bar) or Cmd/Ctrl+G
  useEffect(() => {
    const openHandler = () => setOpen(true);
    const keyHandler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('tc-open-glossary', openHandler);
    window.addEventListener('keydown', keyHandler);
    return () => {
      window.removeEventListener('tc-open-glossary', openHandler);
      window.removeEventListener('keydown', keyHandler);
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100vh' : '88vh',
          bgcolor: 'background.paper',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, pt: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MenuBook sx={{ color: theme.palette.primary.main }} />
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={800}>Stats Glossary</Typography>
            <Typography variant="caption" color="text.secondary">Every metric on The Cycle</Typography>
          </Box>
        </Box>
        <IconButton onClick={() => setOpen(false)} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent ref={contentRef} sx={{ p: { xs: 1.5, sm: 2.5 }, pt: { xs: 1.5, sm: 2 } }}>
        {open && (
          <GlossaryContent variant="dialog" scrollContainerRef={contentRef} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FloatingGlossary;
